import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import FileSaver from 'file-saver';
import CalendarPicker from '../util/CalendarPicker';
import PageToolbar from '../util/PageToolbar';
import SearchFilter, { ActiveFilter } from '../util/SearchFilter';
import ViewToggle from '../util/ViewToggle';
import { ColDef } from '../util/DataTable';
import UserAvatar from '../util/UserAvatar';


interface Props {
  access: any;
  staticDateCompany: any;
  taskOrTasks: any;
}

type ViewMode = 'employee' | 'category' | 'flat';
type QuickRange = 'today' | 'week' | 'lastweek' | '';

const STATUSES = [
  { value: 'fulfilled', label: 'Fulfilled', color: '#059669' },
  { value: 'pending',   label: 'Pending',   color: '#d97706' },
  { value: 'rejected',  label: 'Rejected',  color: '#dc2626' },
];

const CATEGORY_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#f59e0b','#64748b','#10b981','#a855f7'];

function fmtHM(minutes: number, alwaysMinutes = false) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (alwaysMinutes || m === 0 && alwaysMinutes) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function fmtHMFull(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function fmtDuration(start: string, end: string) {
  const mins = moment(end).diff(moment(start), 'minutes');
  return fmtHM(mins);
}


const ReportsNew: React.FC<Props> = ({ access, staticDateCompany }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const { enqueueSnackbar } = useSnackbar();

  // Filters — default: last month
  const [startDate, setStartDate]             = useState<Date>(moment().subtract(1, 'month').startOf('month').toDate());
  const [endDate, setEndDate]                 = useState<Date>(moment().subtract(1, 'month').endOf('month').toDate());
  const [quickRange, setQuickRange]           = useState<QuickRange | ''>('');
  const [allUsers, setAllUsers]               = useState<any[]>([]);
  const [allCategory, setAllCategory]         = useState<any[]>([]);
  const [allTasks, setAllTasks]               = useState<any[]>([]);
  // Category / Employee / Status / Task — во встроенном фильтре SearchFilter (ActiveFilter[])
  const [filters, setFilters]                 = useState<ActiveFilter[]>([]);
  // No draft state — filters apply immediately for live KPI updates

  // Derived filter values
  const catFilter  = filters.find(f => f.colKey === 'category')?.value;
  const userFilter = filters.find(f => f.colKey === 'employee')?.value;

  // Смена категории сбрасывает выбранную задачу (задачи зависят от категории)
  const handleFilters = (next: ActiveFilter[]) => {
    const nextCat = next.find(f => f.colKey === 'category');
    const nextKey = JSON.stringify(nextCat?.values ?? (nextCat?.value ?? null));
    const prevCat = filters.find(f => f.colKey === 'category');
    const prevKey = JSON.stringify(prevCat?.values ?? (prevCat?.value ?? null));
    setFilters(nextKey !== prevKey ? next.filter(f => f.colKey !== 'task') : next);
  };

  // Data
  const [reports, setReports]   = useState<any[]>([]);
  const [loading, setLoading]   = useState(false);

  // View
  const [viewMode, setViewMode]         = useState<ViewMode>('employee');
  const [expandedEmp, setExpandedEmp]   = useState<Set<number>>(new Set());
  const [expandedCat, setExpandedCat]   = useState<Set<number>>(new Set());
  const [search, setSearch]             = useState('');
  const [sortCol, setSortCol]           = useState('');
  const [sortDir, setSortDir]           = useState<'asc' | 'desc'>('asc');
  const [showKPI, setShowKPI]           = useState(true);
  const [subSortCol, setSubSortCol]     = useState('date');
  const [subSortDir, setSubSortDir]     = useState<'asc'|'desc'>('asc');

  const handleSubSort = (col: string) => {
    if (subSortCol === col) setSubSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSubSortCol(col); setSubSortDir('asc'); }
  };
  const subArrow = (col: string) => subSortCol === col
    ? <span style={{ marginLeft: 3, fontSize: 8 }}>{subSortDir === 'asc' ? '▲' : '▼'}</span>
    : <span style={{ marginLeft: 3, fontSize: 8, opacity: .3 }}>⇅</span>;

  const sortSubRows = (rows: any[]) => [...rows].sort((a, b) => {
    const dir = subSortDir === 'asc' ? 1 : -1;
    if (subSortCol === 'date') return dir * (moment(a.startTime).format('YYYYMMDD').localeCompare(moment(b.startTime).format('YYYYMMDD')));
    if (subSortCol === 'time') return dir * (moment.utc(a.startTime).format('HHmm').localeCompare(moment.utc(b.startTime).format('HHmm')));
    if (subSortCol === 'category') {
      const ca = getCatFullPath(allCategory.find((c: any) => c.id === a.categoryId));
      const cb = getCatFullPath(allCategory.find((c: any) => c.id === b.categoryId));
      return dir * ca.localeCompare(cb);
    }
    if (subSortCol === 'hours') return dir * (moment(a.endTime).diff(moment(a.startTime)) - moment(b.endTime).diff(moment(b.startTime)));
    if (subSortCol === 'supervisor') return dir * getSupervisor(a).localeCompare(getSupervisor(b));
    if (subSortCol === 'status') return dir * (a.status || '').localeCompare(b.status || '');
    return 0;
  });

  // Flat list column visibility
  const ALL_FLAT_COLS = ['date','employee','time','hours','category','task','supervisor','notes','status'] as const;
  type FlatCol = typeof ALL_FLAT_COLS[number];
  const [visibleCols, setVisibleCols] = useState<Set<FlatCol>>(new Set(ALL_FLAT_COLS));
  const [colMenuOpen, setColMenuOpen]  = useState(false);
  const colMenuRef = React.useRef<HTMLDivElement>(null);

  // Dots menu (extra options)
  const [dotsOpen, setDotsOpen] = useState(false);
  const dotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dotsRef.current && !dotsRef.current.contains(e.target as Node)) setDotsOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const COL_LABELS: Record<FlatCol, string> = {
    date: 'Date', employee: 'Employee', time: 'Time', hours: 'Hours',
    category: 'Category', task: 'Task', supervisor: 'Supervisor',
    notes: 'Notes', status: 'Status',
  };
  const toggleCol = (col: FlatCol) => {
    setVisibleCols(prev => {
      const n = new Set(prev);
      n.has(col) ? n.delete(col) : n.add(col);
      return n;
    });
  };

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortArrow = (col: string) => sortCol === col
    ? <span style={{ marginLeft: 4, fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
    : <span style={{ marginLeft: 4, fontSize: 9, opacity: .3 }}>⇅</span>;


  // Full category path helper: "Parent > Child > SubChild"
  const getCatFullPath = (cat: any): string => {
    if (!cat) return '';
    const parts: string[] = [cat.name];
    let current = cat;
    while (current.parentId) {
      const parent = allCategory.find((c: any) => c.id === current.parentId);
      if (!parent) break;
      parts.unshift(parent.name);
      current = parent;
    }
    return parts.join(' > ');
  };

  // Колонки-фильтры для встроенного фильтра SearchFilter (Category, Employee, Status, Task)
  const filterColumns: ColDef[] = useMemo(() => {
    // render: () => null — filter-only columns, not rendered in table
    // Order: Category → Task → Employee → Supervisor → Status
    const supervisorOptions = allUsers
      .map((u: any) => ({ value: String(u.id), label: `${u.first_name} ${u.last_name}`.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        key: 'category', label: 'Category', render: () => null, filterType: 'select', multiSelect: true,
        filterOptions: allCategory
          .map((c: any) => ({ value: String(c.id), label: getCatFullPath(c) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: 'task', label: 'Task', render: () => null, filterType: 'select', multiSelect: true,
        filterOptions: allTasks.map((t: any) => ({ value: String(t.id), label: t.name })).sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: 'employee', label: 'Employee', render: () => null, filterType: 'select', multiSelect: true,
        filterOptions: allUsers
          .map((u: any) => ({ value: String(u.id), label: `${u.first_name} ${u.last_name}`.trim() }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: 'supervisor', label: 'Supervisor', render: () => null, filterType: 'select', multiSelect: true,
        filterOptions: supervisorOptions,
      },
      {
        key: 'status', label: 'Status', render: () => null, filterType: 'select', multiSelect: true,
        filterOptions: STATUSES.map(s => ({ value: s.value, label: s.label })),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategory, allUsers, allTasks, filters]);

  // Close column menu on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) setColMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Initial load
  useEffect(() => {
    if (!auth) return;
    (async () => {
      try {
        const [{ data: users }, { data: cats }] = await Promise.all([
          axios.get('users'),
          axios.get('category'),
        ]);
        setAllUsers(users || []);
        setAllCategory(cats || []);
      } catch {}
    })();
  }, [auth]);

  // Load tasks: from selected category/categories, or all if none selected
  useEffect(() => {
    (async () => {
      try {
        const catF    = filters.find(f => f.colKey === 'category');
        const catVals = catF?.values?.length ? catF.values : (catF?.value ? [catF.value] : []);
        const catIds  = catVals.length ? catVals.map(Number) : allCategory.map((c: any) => c.id);
        if (!catIds.length) return;
        const taskSets = await Promise.all(
          catIds.map((id: number) => axios.get(`category/${id}`).then(r => r.data?.task || []))
        );
        const merged = taskSets.flat().filter((t: any) => t.active !== false);
        const unique = Array.from(new Map(merged.map((t: any) => [t.id, t])).values());
        setAllTasks(unique);
      } catch {}
    })();
  }, [catFilter, allCategory, filters]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all records for date range — all filtering done client-side
      // (server-side filters for category/user are not reliably supported by the reports endpoint)
      const body: any = {
        startTime: moment(startDate).startOf('day').format('YYYY-MM-DD'),
        endTime:   moment(endDate).endOf('day').format('YYYY-MM-DD'),
      };
      const { data } = await axios.post('time-reports/reports', body);
      setReports(data || []);
    } catch {}
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { if (auth) fetchReports(); }, [auth, fetchReports]);

  // Quick range
  const applyRange = (r: QuickRange) => {
    setQuickRange(r);
    if (r === 'today') {
      setStartDate(moment().startOf('day').toDate());
      setEndDate(moment().endOf('day').toDate());
    } else if (r === 'week') {
      setStartDate(moment().startOf('isoWeek').toDate());
      setEndDate(moment().endOf('isoWeek').toDate());
    } else if (r === 'lastweek') {
      setStartDate(moment().subtract(1, 'week').startOf('isoWeek').toDate());
      setEndDate(moment().subtract(1, 'week').endOf('isoWeek').toDate());
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const body: any = {
        startTime: moment(startDate).startOf('day').format('YYYY-MM-DD'),
        endTime:   moment(endDate).endOf('day').format('YYYY-MM-DD'),
      };
      const catF    = filters.find(f => f.colKey === 'category');
      const userF   = filters.find(f => f.colKey === 'employee');
      const catVals = catF?.values?.length  ? catF.values  : (catF?.value  ? [catF.value]  : []);
      const usrVals = userF?.values?.length ? userF.values : (userF?.value ? [userF.value] : []);
      if (usrVals.length) body.userIds = usrVals.map(Number);
      const statusF2 = filters.find(f => f.colKey === 'status');
      const taskF2   = filters.find(f => f.colKey === 'task');
      const statusVals2 = statusF2?.values?.length ? statusF2.values : (statusF2?.value ? [statusF2.value] : []);
      const taskVals2   = taskF2?.values?.length   ? taskF2.values   : (taskF2?.value   ? [taskF2.value]   : []);
      if (statusVals2.length) body.status = statusVals2;
      if (catVals.length) body.categoryIds = catVals.map(Number);
      if (taskVals2.length) body.tasks = taskVals2.map(Number);
      const res = await axios.post('time-reports/xls', body, { responseType: 'blob' });
      FileSaver.saveAs(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `reports-${moment(startDate).format('DD.MM')}-${moment(endDate).format('DD.MM.YYYY')}.xlsx`);
    } catch { enqueueSnackbar('Export failed', { variant: 'error' }); }
  };

  // ── Client-side filtering (computed before aggregations) ───────────────────
  const filteredReports = useMemo(() => {
    let r = reports;
    const catF     = filters.find(f => f.colKey === 'category');
    const userF    = filters.find(f => f.colKey === 'employee');
    const catVals  = catF?.values?.length  ? catF.values  : (catF?.value  ? [catF.value]  : []);
    const userVals = userF?.values?.length ? userF.values : (userF?.value ? [userF.value] : []);
    const statusF  = filters.find(f => f.colKey === 'status');
    const taskF    = filters.find(f => f.colKey === 'task');
    const statusVals = statusF?.values?.length ? statusF.values : (statusF?.value ? [statusF.value] : []);
    const taskVals   = taskF?.values?.length   ? taskF.values   : (taskF?.value   ? [taskF.value]   : []);
    const supervisorF    = filters.find(f => f.colKey === 'supervisor');
    const supervisorVals = supervisorF?.values?.length ? supervisorF.values : (supervisorF?.value ? [supervisorF.value] : []);
    if (catVals.length)        r = r.filter((x: any) => catVals.includes(String(x.categoryId)));
    if (taskVals.length)       r = r.filter((x: any) => (x.tasks || []).some((t: any) => taskVals.includes(String(t.id))));
    if (userVals.length)       r = r.filter((x: any) => userVals.includes(String(x.userId || x.user?.id)));
    if (supervisorVals.length) r = r.filter((x: any) => supervisorVals.includes(String(x.supervisorId)));
    if (statusVals.length)     r = r.filter((x: any) => statusVals.includes(x.status));
    return r;
  }, [reports, filters]);
  const displayReports = filteredReports;

  // Filter by search — used both for KPI and flat list
  const filteredFlat = displayReports.filter((r: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = r.user ? `${r.user.first_name} ${r.user.last_name}`.toLowerCase() : '';
    const note = (r.description || '').toLowerCase();
    const cat = allCategory.find((c: any) => c.id === r.categoryId);
    const catN = cat?.name?.toLowerCase() || '';
    return name.includes(q) || note.includes(q) || catN.includes(q);
  });

  // ─── Aggregations (computed from search-filtered data) ────────────
  const totalMins = filteredFlat.reduce((s: number, r: any) => {
    const dur = moment(r.endTime).diff(moment(r.startTime), 'minutes');
    return s + (dur > 0 ? dur : 0);
  }, 0);

  const statusCounts = STATUSES.map(st => ({
    ...st,
    count: filteredFlat.filter((r: any) => r.status === st.value).length,
    pct: filteredFlat.length ? Math.round(filteredFlat.filter((r: any) => r.status === st.value).length / filteredFlat.length * 100) : 0,
  }));
  const maxStatus = Math.max(1, ...statusCounts.map(s => s.count));

  // By category
  const catMap: Record<string, { name: string; mins: number; color: string; idx: number }> = {};
  filteredFlat.forEach((r: any) => {
    const cat = allCategory.find((c: any) => c.id === r.categoryId);
    if (!cat) return;
    if (!catMap[cat.id]) catMap[cat.id] = { name: getCatFullPath(cat), mins: 0, color: '', idx: Object.keys(catMap).length };
    catMap[cat.id].mins += Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
  });
  const catList = Object.entries(catMap)
    .map(([id, v]) => ({ id, ...v, color: CATEGORY_COLORS[v.idx % CATEGORY_COLORS.length] }))
    .sort((a, b) => b.mins - a.mins);
  const maxCat = Math.max(1, ...catList.map(c => c.mins));

  // By task
  const taskMap: Record<string, { name: string; catName: string; mins: number; idx: number }> = {};
  filteredFlat.forEach((r: any) => {
    const taskArr: any[] = r.tasks || [];
    if (!taskArr.length) return;
    taskArr.forEach((tk: any) => {
      const key = tk.id;
      const cat = allCategory.find((c: any) => c.id === r.categoryId);
      if (!taskMap[key]) taskMap[key] = { name: tk.name, catName: cat?.name || '', mins: 0, idx: Object.keys(taskMap).length };
      taskMap[key].mins += Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
    });
  });
  const taskList = Object.entries(taskMap)
    .map(([id, v]) => ({ id, ...v, color: CATEGORY_COLORS[v.idx % CATEGORY_COLORS.length] }))
    .sort((a, b) => b.mins - a.mins).slice(0, 10);
  const maxTask = Math.max(1, ...taskList.map(t => t.mins));

  // Group by employee — uid stored explicitly to avoid undefined from emp.user?.id
  const empMap: Record<number, { uid: number; user: any; reports: any[]; totalMins: number; statuses: Record<string, number> }> = {};
  filteredFlat.forEach((r: any) => {
    const uid = r.userId || r.user?.id;
    if (!uid) return;
    if (!empMap[uid]) empMap[uid] = { uid, user: r.user || allUsers.find((u: any) => u.id === uid), reports: [], totalMins: 0, statuses: {} };
    const m = Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
    empMap[uid].totalMins += m;
    empMap[uid].reports.push(r);
    empMap[uid].statuses[r.status] = (empMap[uid].statuses[r.status] || 0) + 1;
  });
  const getSupervisor = (r: any): string => {
    if (r.supervisor) return `${r.supervisor.first_name || ''} ${r.supervisor.last_name || ''}`.trim();
    return '';
  };

  // Sort empList
  const empListSorted = Object.values(empMap).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'name') {
      const la = `${a.user?.first_name}${a.user?.last_name}`.toLowerCase();
      const lb = `${b.user?.first_name}${b.user?.last_name}`.toLowerCase();
      return dir * la.localeCompare(lb);
    }
    if (sortCol === 'hours') return dir * (a.totalMins - b.totalMins);
    if (sortCol === 'supervisor') {
      const sa = a.reports[0] ? getSupervisor(a.reports[0]) : '';
      const sb = b.reports[0] ? getSupervisor(b.reports[0]) : '';
      return dir * sa.localeCompare(sb);
    }
    if (sortCol === 'status') {
      const statusOrder: Record<string, number> = { fulfilled: 0, pending: 1, rejected: 2, new: 3 };
      const sa = Object.keys(a.statuses)[0] || '';
      const sb = Object.keys(b.statuses)[0] || '';
      return dir * ((statusOrder[sa] ?? 9) - (statusOrder[sb] ?? 9));
    }
    return b.totalMins - a.totalMins;
  });
  const empList = empListSorted;

  // Group by category (for table)
  const catTableMap: Record<number, { cat: any; reports: any[]; totalMins: number }> = {};
  filteredFlat.forEach((r: any) => {
    const cat = allCategory.find((c: any) => c.id === r.categoryId);
    const cid = cat?.id || 0;
    if (!catTableMap[cid]) catTableMap[cid] = { cat: cat || { name: 'Uncategorized', id: 0 }, reports: [], totalMins: 0 };
    catTableMap[cid].totalMins += Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
    catTableMap[cid].reports.push(r);
  });
  const catTableList = Object.values(catTableMap).sort((a, b) => b.totalMins - a.totalMins);

  const empUniq = Array.from(new Set(filteredFlat.map((r: any) => r.userId || r.user?.id).filter(Boolean)));

  const dateFormat = staticDateCompany ? staticDateCompany.toLowerCase().replace(/mm/g, 'MM') : 'dd/MM/yyyy';

  // ─── Styles ──────────────────────────────────────────────────────
  const pill = (): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
    borderRadius: 8, padding: '4px 10px', cursor: 'pointer', userSelect: 'none',
  });
  const pillLbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar-week)' };

  const card: React.CSSProperties = { padding: '8px 14px' };
  const kpiLbl: React.CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 2 };
  const kpiVal: React.CSSProperties = { fontSize: 22, fontWeight: 900, letterSpacing: '-.02em', color: 'var(--text-color)', lineHeight: 1 };

  const qrBtn = (active: boolean): React.CSSProperties => ({
    padding: '4px 9px', borderRadius: 7,
    border: `1px solid ${active ? 'rgba(37,99,235,.3)' : 'var(--border-input)'}`,
    background: active ? 'rgba(37,99,235,.08)' : 'var(--btn-user-form)',
    color: active ? '#2563eb' : 'var(--text-color-calendar-week)',
    fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  });

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
    fontSize: 12, fontWeight: active ? 800 : 600,
    color: active ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
    borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`,
    marginBottom: -1, whiteSpace: 'nowrap' as const, position: 'relative' as const,
  });

  const statusTag = (status: string) => {
    const map: any = {
      fulfilled: { bg: 'rgba(5,150,105,.1)', color: '#059669' },
      pending:   { bg: 'rgba(217,119,6,.1)', color: '#d97706' },
      rejected:  { bg: 'rgba(220,38,38,.1)', color: '#dc2626' },
      new:       { bg: 'rgba(37,99,235,.1)', color: '#2563eb' },
    };
    const s = map[status] || map.new;
    return (
      <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
        background: s.bg, color: s.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <span style={{ fontSize: 7 }}>●</span>{status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const empStatusTag = (statuses: Record<string, number>) => {
    if (statuses.rejected > 0) return <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,.08)', borderRadius: 20, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ fontSize: 7 }}>●</span>{statuses.rejected} rejected</span>;
    if (statuses.pending > 0)  return <span style={{ fontSize: 9, fontWeight: 700, color: '#d97706', background: 'rgba(217,119,6,.08)', borderRadius: 20, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ fontSize: 7 }}>●</span>{statuses.pending} pending</span>;
    return <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: 'rgba(5,150,105,.08)', borderRadius: 20, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ fontSize: 7 }}>●</span>All approved</span>;
  };

  // Sorted flat list
  const filteredFlatSorted = [...filteredFlat].sort((a: any, b: any) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortCol === 'date') return dir * (moment(a.startTime).valueOf() - moment(b.startTime).valueOf());
    if (sortCol === 'employee') {
      const na = a.user ? `${a.user.first_name}${a.user.last_name}` : '';
      const nb = b.user ? `${b.user.first_name}${b.user.last_name}` : '';
      return dir * na.localeCompare(nb);
    }
    if (sortCol === 'hours' || sortCol === 'duration') return dir * (moment(a.endTime).diff(moment(a.startTime)) - moment(b.endTime).diff(moment(b.startTime)));
    if (sortCol === 'status') return dir * (a.status || '').localeCompare(b.status || '');
    if (sortCol === 'supervisor') return dir * getSupervisor(a).localeCompare(getSupervisor(b));
    return 0;
  });

  const thStyle: React.CSSProperties = {
    padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 800,
    letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)',
    borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap',
    background: 'var(--bg-top)', cursor: 'pointer', userSelect: 'none',
  };
  const tdStyle: React.CSSProperties = {
    padding: '9px 12px', borderBottom: '1px solid var(--border-input)',
    fontSize: 12, color: 'var(--text-color-calendar-week)', verticalAlign: 'middle',
    maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  // td with tooltip — for text that may be truncated
  const tdT = (text: string, extra?: React.CSSProperties) => ({
    style: { ...tdStyle, ...extra },
    title: text,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: 'var(--page-height)', overflow: 'hidden' }}>

      {/* ── TOOLBAR ── */}
      <PageToolbar mb={0}>

        {/* Search + встроенные фильтры (Category, Employee, Status, Task) в одном компоненте поиска */}
        <SearchFilter
          grow
          columns={filterColumns}
          search={search}
          onSearch={setSearch}
          filters={filters}
          onFilters={handleFilters}
        />

        {/* Date range */}
        <CalendarPicker
          mode="range"
          startDate={startDate}
          endDate={endDate}
          onChange={(s, e) => { if (s) setStartDate(s); if (e) setEndDate(e); }}
          displayFormat="DD MMM YYYY"
        />

        <div style={{ flex: 1 }} />

        {/* KPI toggle */}
        <button type="button" onClick={() => setShowKPI(v => !v)}
          title={showKPI ? 'Hide KPI' : 'Show KPI'}
          style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px',
            borderRadius: 7, border: '1px solid var(--border-input)', cursor: 'pointer', fontSize: 11, fontWeight: 700,
            background: showKPI ? 'rgba(37,99,235,.1)' : 'var(--btn-user-btn)',
            color: showKPI ? '#2563eb' : 'var(--text-color-calendar-week)',
            transition: 'all .15s',
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          KPI
        </button>

        <div style={{ width: 1, height: 22, background: 'var(--border-input)', flexShrink: 0 }} />

        {/* View toggle */}
        <ViewToggle
          value={viewMode}
          onChange={v => setViewMode(v as ViewMode)}
          options={[
            { value: 'employee', label: 'user',     icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.85"/></svg> },
            { value: 'category', label: 'category', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg> },
            { value: 'flat',     label: 'list',     icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
          ]}
        />

        {/* Dots menu — extra options */}
        <div style={{ position: 'relative' }} ref={dotsRef}>
          <button type="button" onClick={() => setDotsOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: 7, border: '1px solid var(--border-input)',
              background: dotsOpen ? 'var(--btn-user-form)' : 'var(--btn-user-btn)',
              color: 'var(--text-color-calendar-week)', cursor: 'pointer', fontSize: 14, flexShrink: 0 }}
            title="More options">
            ···
          </button>
          {dotsOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0,
              zIndex: 'var(--z-dropdown)' as any,
              background: 'var(--bg-form)', border: '1px solid var(--border-input)',
              borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
              minWidth: 160, padding: '4px 0' }}>
              {access?.timesheet_reports_export && (
                <button type="button"
                  onClick={() => { setDotsOpen(false); handleExport(); }}
                  disabled={displayReports.length === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px',
                    border: 'none', background: 'transparent', cursor: displayReports.length === 0 ? 'default' : 'pointer',
                    fontSize: 12, color: displayReports.length === 0 ? 'var(--text-color-calendar-week)' : 'var(--text-color)',
                    textAlign: 'left' }}
                  onMouseEnter={e => { if (displayReports.length > 0) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-top)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export XLS
                </button>
              )}
            </div>
          )}
        </div>

      </PageToolbar>

      {/* ── KPI ROW — hidden when showKPI=false ── */}
      {showKPI && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.6fr 2fr 2fr', gap: 10, flexShrink: 0 }}>

        {/* Total Hours */}
        <div className="dashboard-card" style={card}>
          <div style={kpiLbl}>Total Hours</div>
          <div style={{ ...kpiVal, color: '#059669' }}>{fmtHM(totalMins)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 4 }}>
            {displayReports.length} entries
          </div>
        </div>

        {/* Employees */}
        <div className="dashboard-card" style={card}>
          <div style={kpiLbl}>Employees</div>
          <div style={{ ...kpiVal, color: '#2563eb' }}>{empUniq.length}</div>
          <div style={{ display: 'flex', marginTop: 6 }}>
            {empList.slice(0, 4).map((e, i) => (
              <div key={i} style={{ marginLeft: i === 0 ? 0 : -5, flexShrink: 0 }}>
                <UserAvatar user={e.user ?? {}} size={22} />
              </div>
            ))}
            {empUniq.length > 4 && (
              <div style={{ width: 22, height: 22, borderRadius: 6, border: '2px solid var(--border-input)',
                background: 'var(--btn-user-form)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 7, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginLeft: -5 }}>
                +{empUniq.length - 4}
              </div>
            )}
          </div>
        </div>

        {/* By Status */}
        <div className="dashboard-card" style={card}>
          <div style={kpiLbl}>By Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
            {statusCounts.map(s => (
              <div key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)', flex: 1 }}>{s.label}</span>
                <div style={{ flex: 1, height: 4, background: 'var(--border-input)', borderRadius: 2, overflow: 'hidden', maxWidth: 64 }}>
                  <div style={{ width: `${(s.count / maxStatus) * 100}%`, height: '100%', background: s.color, borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 900, color: s.color, minWidth: 24, textAlign: 'right' }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Category */}
        <div className="dashboard-card" style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={kpiLbl}>By Category</div>
            <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{catList.length} total</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, overflowY: 'auto', maxHeight: 88 }}>
            {catList.length === 0 && <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>No data</span>}
            {catList.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 7, height: 7, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                <div style={{ width: 64, height: 4, background: 'var(--border-input)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{ width: `${(c.mins / maxCat) * 100}%`, height: '100%', background: c.color, borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>{fmtHMFull(c.mins)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Task */}
        <div className="dashboard-card" style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={kpiLbl}>By Task</div>
            <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{taskList.length} tasks</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, overflowY: 'auto', maxHeight: 88 }}>
            {taskList.length === 0 && <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>No data</span>}
            {taskList.map(tk => (
              <div key={tk.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: tk.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tk.name}</span>
                <div style={{ width: 64, height: 4, background: 'var(--border-input)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{ width: `${(tk.mins / maxTask) * 100}%`, height: '100%', background: tk.color, borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>{fmtHMFull(tk.mins)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* ── ENTRIES TABLE ── */}
      <div className="dashboard-card" style={{ padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Table area */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>Loading...</span>
            </div>
          )}

          {/* BY EMPLOYEE */}
          {!loading && viewMode === 'employee' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <tbody>
                {empList.filter(e => !search || `${e.user?.first_name} ${e.user?.last_name}`.toLowerCase().includes(search.toLowerCase())).map(emp => {
                  const uid = emp.uid;
                  const isOpen = expandedEmp.has(uid);
                  const name = `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim();
                  return (
                    <React.Fragment key={uid}>
                      {/* ── Parent row ── */}
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedEmp(prev => {
                        const n = new Set(prev); n.has(uid) ? n.delete(uid) : n.add(uid); return n;
                      })}>
                        <td style={tdStyle}>
                          <button type="button" style={{ width: 20, height: 20, borderRadius: 5,
                            border: '1px solid var(--border-input)', background: isOpen ? 'rgba(37,99,235,.08)' : 'var(--btn-user-form)',
                            color: isOpen ? '#2563eb' : 'var(--text-color-calendar-week)', fontSize: 9, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                        </td>
                        {/* Name — width:99% pushes hours/status to right, name stays left */}
                        <td style={{ ...tdStyle, width: '99%', maxWidth: 'none', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                            <UserAvatar user={emp.user ?? { id: uid }} size={28} />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }}>{name}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{emp.user?.job_title || ''}</div>
                            </div>
                          </div>
                        </td>
                        {/* Status left of hours */}
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', maxWidth: 'none', textAlign: 'right' }}>{empStatusTag(emp.statuses)}</td>
                        {/* Hours — rightmost */}
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', maxWidth: 'none', textAlign: 'right', paddingRight: 20 }}><span style={{ fontSize: 13, fontWeight: 900, color: 'var(--text-color)' }}>{fmtHMFull(emp.totalMins)}</span></td>
                      </tr>

                      {/* ── Expanded: nested table with own header + sort ── */}
                      {isOpen && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0, background: 'rgba(37,99,235,.018)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-input)' }}>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', paddingLeft: 44, cursor: 'pointer' }} onClick={() => handleSubSort('category')}>Category{subArrow('category')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('date')}>Date{subArrow('date')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('time')}>Time{subArrow('time')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('hours')}>Hours{subArrow('hours')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer' }} onClick={() => handleSubSort('supervisor')}>Supervisor{subArrow('supervisor')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer' }} onClick={() => handleSubSort('status')}>Status{subArrow('status')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortSubRows(emp.reports).map((r: any) => {
                                  const dur = Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
                                  const cat = allCategory.find((c: any) => c.id === r.categoryId);
                                  const catPath = getCatFullPath(cat);
                                  return (
                                    <tr key={r.id}>
                                      <td style={{ ...tdStyle, paddingLeft: 44, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={catPath}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,.07)', padding: '2px 7px', borderRadius: 4 }}>{catPath || '—'}</span>
                                      </td>
                                      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {moment(r.startTime).format('DD.MM.YYYY')} <span style={{ color: 'var(--text-color-calendar-week)' }}>{moment(r.startTime).format('ddd')}</span>
                                      </td>
                                      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {moment.utc(r.startTime).format('HH:mm')} – {moment.utc(r.endTime).format('HH:mm')}
                                      </td>
                                      <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>{fmtHMFull(dur)}</td>
                                      <td {...tdT(getSupervisor(r), { fontSize: 12 })}>{getSupervisor(r)}</td>
                                      <td style={tdStyle}>{statusTag(r.status)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {empList.length === 0 && !loading && (
                  <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', padding: 32 }}>No data for selected period</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* BY CATEGORY */}
          {!loading && viewMode === 'category' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <tbody>
                {catTableList.filter(c => !search || c.cat.name.toLowerCase().includes(search.toLowerCase())).map((c, ci) => {
                  const cid = c.cat.id;
                  const isOpen = expandedCat.has(cid);
                  const color = CATEGORY_COLORS[ci % CATEGORY_COLORS.length];
                  const pct = totalMins ? Math.round(c.totalMins / totalMins * 100) : 0;
                  return (
                    <React.Fragment key={cid}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedCat(prev => {
                        const n = new Set(prev); n.has(cid) ? n.delete(cid) : n.add(cid); return n;
                      })}>
                        <td style={tdStyle}>
                          <button type="button" style={{ width: 20, height: 20, borderRadius: 5,
                            border: '1px solid var(--border-input)', background: isOpen ? 'rgba(37,99,235,.08)' : 'var(--btn-user-form)',
                            color: isOpen ? '#2563eb' : 'var(--text-color-calendar-week)', fontSize: 9, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                        </td>
                        {/* Category name — width:99% keeps it left-aligned */}
                        <td style={{ ...tdStyle, width: '99%', maxWidth: 'none', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }} title={getCatFullPath(c.cat)}>{getCatFullPath(c.cat)}</span>
                          </div>
                        </td>
                        {/* Hours — right aligned, no percentage */}
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', maxWidth: 'none', textAlign: 'right' }}><span style={{ fontSize: 13, fontWeight: 900, color: 'var(--text-color)' }}>{fmtHMFull(c.totalMins)}</span></td>
                        {/* Progress bar only (no %) */}
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', maxWidth: 'none', textAlign: 'right' }}>
                          <div style={{ width: 80, height: 5, background: 'var(--border-input)', borderRadius: 3, overflow: 'hidden', display: 'inline-block' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
                          </div>
                        </td>
                      </tr>
                      {/* Nested table with own sortable header */}
                      {isOpen && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0, background: 'rgba(37,99,235,.018)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(37,99,235,.12)' }}>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', paddingLeft: 44, cursor: 'pointer' }} onClick={() => handleSubSort('employee')}>Employee{subArrow('employee')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('date')}>Date{subArrow('date')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('time')}>Time{subArrow('time')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSubSort('hours')}>Hours{subArrow('hours')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer' }} onClick={() => handleSubSort('supervisor')}>Supervisor{subArrow('supervisor')}</th>
                                  <th style={{ ...thStyle, background: 'rgba(37,99,235,.05)', cursor: 'pointer' }} onClick={() => handleSubSort('status')}>Status{subArrow('status')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortSubRows(c.reports).map((r: any) => {
                                  const dur = Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
                                  const empName = r.user ? `${r.user.first_name} ${r.user.last_name}` : '';
                                  return (
                                    <tr key={r.id}>
                                      <td style={{ ...tdStyle, paddingLeft: 44, fontWeight: 600, color: 'var(--text-color)' }}>{empName || '—'}</td>
                                      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {moment(r.startTime).format('DD.MM.YYYY')} <span style={{ color: 'var(--text-color-calendar-week)' }}>{moment(r.startTime).format('ddd')}</span>
                                      </td>
                                      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap' }}>{moment.utc(r.startTime).format('HH:mm')} – {moment.utc(r.endTime).format('HH:mm')}</td>
                                      <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>{fmtHMFull(dur)}</td>
                                      <td {...tdT(getSupervisor(r), { fontSize: 12 })}>{getSupervisor(r)}</td>
                                      <td style={tdStyle}>{statusTag(r.status)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {catTableList.length === 0 && (
                  <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', padding: 32 }}>No data</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* FLAT LIST */}
          {!loading && viewMode === 'flat' && (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    {visibleCols.has('date')       && <th style={{ ...thStyle, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('date')}>Date{sortArrow('date')}</th>}
                    {visibleCols.has('employee')    && <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('employee')}>Employee{sortArrow('employee')}</th>}
                    {visibleCols.has('time')        && <th style={{ ...thStyle, whiteSpace: 'nowrap' }}>Time</th>}
                    {visibleCols.has('hours')       && <th style={{ ...thStyle, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('hours')}>Hours{sortArrow('hours')}</th>}
                    {visibleCols.has('category')    && <th style={thStyle}>Category</th>}
                    {visibleCols.has('task')        && <th style={thStyle}>Task</th>}
                    {visibleCols.has('supervisor')  && <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('supervisor')}>Supervisor{sortArrow('supervisor')}</th>}
                    {visibleCols.has('notes')       && <th style={thStyle}>Notes</th>}
                    {visibleCols.has('status')      && <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortArrow('status')}</th>}
                    {/* Columns icon — last header cell */}
                    <th style={{ ...thStyle, width: 32, cursor: 'default', padding: '8px 8px' }}>
                      <div style={{ position: 'relative' }} ref={colMenuRef}>
                      <button type="button" onClick={() => setColMenuOpen(o => !o)} title="Toggle columns"
                        style={{ width: 22, height: 22, borderRadius: 5, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: colMenuOpen ? 'rgba(37,99,235,.15)' : 'var(--btn-user-form)', color: colMenuOpen ? '#2563eb' : 'var(--text-color-calendar-week)' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                        </svg>
                      </button>
                      {colMenuOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 'var(--z-dropdown)' as any,
                          background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 5,
                          boxShadow: '0 4px 16px rgba(0,0,0,.12)', padding: '6px 0', minWidth: 160 }}>
                          {ALL_FLAT_COLS.map(col => (
                            <label key={col} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', cursor: 'pointer',
                              fontSize: 12, color: 'var(--text-color)', userSelect: 'none' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-top)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                              <input type="checkbox" checked={visibleCols.has(col)} onChange={() => toggleCol(col)}
                                style={{ accentColor: '#2563eb', width: 13, height: 13 }} />
                              {COL_LABELS[col]}
                            </label>
                          ))}
                        </div>
                      )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFlatSorted.map((r: any) => {
                    const cat = allCategory.find((c: any) => c.id === r.categoryId);
                    const catPath = getCatFullPath(cat);
                    const dur = Math.max(0, moment(r.endTime).diff(moment(r.startTime), 'minutes'));
                    const empName = r.user ? `${r.user.first_name} ${r.user.last_name}` : '';
                    const taskNames = (r.tasks || []).map((tk: any) => tk.name).filter(Boolean).join(', ');
                    return (
                      <tr key={r.id}>
                        {visibleCols.has('date') && (
                          <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>
                            {moment(r.startTime).format('DD.MM.YYYY')} <span style={{ color: 'var(--text-color-calendar-week)', fontWeight: 400 }}>{moment(r.startTime).format('ddd')}</span>
                          </td>
                        )}
                        {visibleCols.has('employee') && (
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <UserAvatar user={r.user ?? { id: r.userId }} size={22} />
                              <span style={{ fontSize: 12, color: 'var(--text-color)' }}>{empName}</span>
                            </div>
                          </td>
                        )}
                        {visibleCols.has('time') && (
                          <td style={{ ...tdStyle, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {moment.utc(r.startTime).format('HH:mm')} – {moment.utc(r.endTime).format('HH:mm')}
                          </td>
                        )}
                        {visibleCols.has('hours') && (
                          <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-color)', whiteSpace: 'nowrap' }}>{fmtHMFull(dur)}</td>
                        )}
                        {visibleCols.has('category') && (
                          <td style={{ ...tdStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }} title={catPath}>
                            {catPath ? <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,.08)', padding: '2px 7px', borderRadius: 5 }}>{catPath}</span> : '—'}
                          </td>
                        )}
                        {visibleCols.has('task') && (
                          <td style={{ ...tdStyle, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }} title={taskNames}>{taskNames}</td>
                        )}
                        {visibleCols.has('supervisor') && (
                          <td {...tdT(getSupervisor(r), { fontSize: 12 })}>{getSupervisor(r)}</td>
                        )}
                        {visibleCols.has('notes') && (
                          <td style={{ ...tdStyle, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }} title={r.description || ''}>{r.description || ''}</td>
                        )}
                        {visibleCols.has('status') && (
                          <td style={tdStyle}>{statusTag(r.status)}</td>
                        )}
                        <td style={{ ...tdStyle, width: 32, padding: '8px 8px' }}></td>{/* spacer for column icon */}
                      </tr>
                    );
                  })}
                  {filteredFlatSorted.length === 0 && (
                    <tr><td colSpan={visibleCols.size + 1} style={{ ...tdStyle, textAlign: 'center', padding: 32 }}>No data</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', borderTop: '1px solid var(--border-input)',
          background: 'var(--bg-top)', flexShrink: 0, fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
          <span>
            {viewMode === 'employee' && `${empList.length} employees`}
            {viewMode === 'category' && `${catTableList.length} categories`}
            {viewMode === 'flat' && `${filteredFlat.length} of ${displayReports.length} entries`}
          </span>
          <span style={{ fontWeight: 600, color: 'var(--text-color)' }}>
            Total: {fmtHM(totalMins)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReportsNew;
